import type { Notebook } from "@ronin/contracts";
import { Button, cn, Dialog, Input, Label } from "@ronin/ui";
import { Check, Folder, FolderX } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { buildTree, type TreeNode } from "./notebook-tree.js";

export type NotebookDialogState =
  | { mode: "closed" }
  | { mode: "create"; parentId: string | null; parentName?: string }
  | { mode: "rename"; notebook: Notebook };

/** Create or rename a notebook (spec04 FR5). */
export function NotebookDialog({
  state,
  onClose,
  onSubmit,
}: {
  state: NotebookDialogState;
  onClose: () => void;
  onSubmit: (name: string) => Promise<unknown>;
}) {
  const title =
    state.mode === "rename"
      ? "Rename notebook"
      : state.mode === "create" && state.parentName
        ? "New sub-notebook"
        : "New notebook";
  return (
    <Dialog
      open={state.mode !== "closed"}
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={
        state.mode === "create" && state.parentName ? `Inside “${state.parentName}”.` : undefined
      }
    >
      {state.mode !== "closed" && (
        <NameForm
          key={state.mode === "rename" ? state.notebook.id : `new-${state.parentId}`}
          initial={state.mode === "rename" ? state.notebook.name : ""}
          submitLabel={state.mode === "rename" ? "Rename" : "Create notebook"}
          onCancel={onClose}
          onSubmit={onSubmit}
        />
      )}
    </Dialog>
  );
}

function NameForm({
  initial,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: string;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (name: string) => Promise<unknown>;
}) {
  const id = useId();
  const [name, setName] = useState(initial);
  const [pending, setPending] = useState(false);
  const canSave = name.trim() !== "" && !pending;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    setPending(true);
    try {
      await onSubmit(name.trim());
    } catch {
      // The error toast comes from the mutation; keep the dialog open.
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-name`}>Name</Label>
        <Input
          id={`${id}-name`}
          value={name}
          maxLength={100}
          placeholder="e.g. Work"
          data-autofocus
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** "Move to…" (spec04 FR6): pick a notebook or "No notebook". */
export function MoveNoteDialog({
  open,
  notebooks,
  currentId,
  onClose,
  onMove,
}: {
  open: boolean;
  notebooks: readonly Notebook[];
  currentId: string | null;
  onClose: () => void;
  onMove: (notebookId: string | null) => void;
}) {
  const flat: TreeNode[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const node of nodes) {
      flat.push(node);
      walk(node.children);
    }
  };
  walk(buildTree(notebooks));
  const option = (id: string | null, label: string, depth: number, Icon: typeof Folder) => (
    <li key={id ?? "none"}>
      <button
        type="button"
        onClick={() => onMove(id)}
        aria-current={id === currentId ? "true" : undefined}
        className={cn(
          "flex h-8 w-full items-center gap-2 pr-2 text-left text-sm text-secondary-foreground hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
          id === currentId && "text-foreground",
        )}
        style={{ paddingLeft: 8 + (depth - 1) * 16 }}
      >
        <Icon className="size-3.5 shrink-0 text-dim-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {id === currentId && <Check className="size-3.5 text-brand" aria-label="Current" />}
      </button>
    </li>
  );
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()} title="Move to notebook">
      <ul className="flex max-h-80 flex-col overflow-y-auto border">
        {option(null, "No notebook", 1, FolderX)}
        {flat.map((node) => option(node.notebook.id, node.notebook.name, node.depth, Folder))}
      </ul>
      <div className="mt-2 flex justify-end">
        <Button type="button" variant="ghost" onClick={onClose} data-autofocus>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}

/** Irreversible, so it asks first (DESIGN.md "Undo over confirm"). */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={title}
      description={description}
    >
      <div className="mt-2 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose} data-autofocus>
          Cancel
        </Button>
        <Button type="button" variant="destructive" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
